import { CheckboxGroup, CheckboxGroupOption, Empty, IconButton, Id, Loading, TextControl } from "@dgs/core";
import { useField } from "formik";
import { FC, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Virtuoso, VirtuosoHandle } from "react-virtuoso";
import styled from "styled-components";

export interface BaseOption {
	id: Id;
	name: string;
}

interface Props {
	label: string;
	empty: string;
	name: string;
	options: BaseOption[];
	search: string;
	onSearch: (search: string) => void;
	endReached: () => void;
	loading?: boolean;
	optionsToHide?: BaseOption[];
}

type SelectMode = "all" | "selected";

const Wrapper = styled.div`
	padding-bottom: ${({ theme }) => theme.spacing(4)};
	display: flex;
	flex-direction: column;
	height: 100%;
`;

const ToggleIconButton = styled(IconButton)`
	border-radius: 0;
`;

const ToggleWrapper = styled.div`
	display: flex;
	border-radius: 2px;
	overflow: hidden;
`;

const Header = styled.div`
	display: grid;
	gap: ${({ theme }) => theme.spacing(2)};
	grid-template-columns: 1fr max-content;
	align-items: center;
`;

const ListWrapper = styled.div`
	flex: 1;
`;

const CheckboxWrapper = styled.div`
	padding: ${({ theme }) => theme.spacing(1)};
`;

export const CheckboxMultiSelect: FC<Props> = ({
	label,
	empty,
	name,
	options,
	endReached,
	onSearch,
	search,
	loading,
	optionsToHide = [],
}) => {
	const { t } = useTranslation();
	const [field] = useField<Id[]>(name);
	const [mode, setMode] = useState<SelectMode>("all");
	const virtuosoRef = useRef<VirtuosoHandle | null>(null);

	const currentOptions = useMemo(() => {
		return options.filter(
			(x) => (mode === "all" || field.value.includes(x.id)) && !optionsToHide.some((y) => y.id === x.id),
		);
	}, [field.value, mode, options, optionsToHide]);

	return (
		<Wrapper>
			<Header>
				<TextControl
					placeholder={t("Search...")}
					name={`${name}.search`}
					label={label}
					onChange={(e) => onSearch(e.target.value)}
					value={search}
					suffix={loading && <Loading variant="tiny" />}
				/>
				<ToggleWrapper>
					<ToggleIconButton
						type="button"
						className={mode === "all" ? "active" : ""}
						onClick={() => setMode("all")}
						title={t("All")}
						color={mode === "all" ? "primary" : "default"}
						icon="list"
					/>
					<ToggleIconButton
						type="button"
						className={mode === "selected" ? "active" : ""}
						onClick={() => setMode("selected")}
						title={t("Selected")}
						color={mode === "selected" ? "primary" : "default"}
						iconSrc="/checklist_rtl.svg"
					/>
				</ToggleWrapper>
			</Header>
			{!loading && currentOptions.length === 0 && <Empty>{empty}</Empty>}
			<ListWrapper role="listbox">
				<CheckboxGroup name={name}>
					<Virtuoso
						style={{ height: "100%" }}
						ref={virtuosoRef}
						data={currentOptions}
						endReached={endReached}
						computeItemKey={(index) => (currentOptions[index] ? currentOptions[index].id : index)}
						itemContent={(index, option) => (
							<CheckboxWrapper>
								<CheckboxGroupOption label={option.name} value={option.id} />
							</CheckboxWrapper>
						)}
					/>
				</CheckboxGroup>
			</ListWrapper>
		</Wrapper>
	);
};
